import "./din276Tree.css";
import { useEffect, useId, useMemo, useRef, useState, useCallback, Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
  Boxes, Box, Folder, Package, Frame, Sigma,
  Search, ChevronsDownUp, ChevronsUpDown,
} from "lucide-react";
import Section from "../../components/Section";
import { Row, classKey, partKey, type RowCtx } from "./Din276TreeRow";
import { authenticatedFetch } from "../../JWTAuthService";
import { configuration } from "../../Configuration";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { TooltipProvider } from "@/components/ui/tooltip";
import { buildTree, type DinTree } from "./din276Tree";

// The K.*/T.*/F.* ontology layer — Komponenten / Topologie / Funktionen. These
// are not productive DIN-276 cost groups; the "Produktiv/Alles" filter hides
// them as standalone roots (they stay reachable as composition parts).
const SYNTHETIC_KINDS = new Set(["component", "locus", "funktion"]);


export default function DIN276TreePanel() {
  const { t } = useTranslation();
  const searchId = useId();
  const [raw, setRaw] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tree: DinTree | null = useMemo(() => (raw ? buildTree(raw) : null), [raw]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [whereOpen, setWhereOpen] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // Two INDEPENDENT post-`--apply` HITL filters, both default off (hidden):
  //  · showSynthetic — the K.*/T.*/F.* ontology layer (kind-based)
  //  · showPending   — provisional placeholder nodes (synthetic:true)
  const [showSynthetic, setShowSynthetic] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch fresh (bypass the shared getDIN276Metadata in-memory cache) so the
        // admin tool always reflects the current server-side metadata.
        // Fetch EVERYTHING (both server filters off) — this admin tree is the one
        // place that wants the full graph; the two independent toggles below hide
        // the synthetic layer / provisional nodes client-side, without breaking
        // the composition (parts) drilldown. The norm only (`normOnly`): a customer's own
        // attributes are no part of the taxonomy administered here.
        const resp = await authenticatedFetch(
          `${configuration.serverUrl}/metadata/din276?excludeSynthetic=false&excludePending=false&normOnly=true`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          },
        );
        if (!resp || !resp.ok) throw new Error(`metadata/din276 ${resp?.status ?? "failed"}`);
        const data = await resp.json();
        if (!cancelled) setRaw(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "load failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The "one level open" baseline — only the real numeric DIN roots. Synthetic
  // K./F./T. types are themselves roots (level 0); force-opening them would leave
  // them permanently expanded, so "Alle zu"/default keep them collapsed while
  // "Alle auf" still opens them explicitly.
  const baselineRoots = useCallback(
    () => (tree ? tree.roots.filter((c) => !SYNTHETIC_KINDS.has(tree.byCode[c]?.kind)).map(classKey) : []),
    [tree],
  );

  // Default: top DIN level (roots) open one level.
  useEffect(() => {
    if (tree) setExpanded(new Set(baselineRoots()));
  }, [tree, baselineRoots]);

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const classAncestors = useCallback(
    (code: string): string[] => {
      const out: string[] = [];
      let c: string | undefined = code;
      const seen = new Set<string>();
      while (c && tree?.parentOf[c] && !seen.has(c)) {
        seen.add(c);
        c = tree.parentOf[c];
        if (c) out.push(c);
      }
      return out;
    },
    [tree],
  );

  // Reveal a (possibly K.* part-only) code; returns its render key and expands ancestors.
  const ensureRevealed = useCallback(
    (code: string, add: Set<string>, depthGuard = 0): string | null => {
      if (!tree || depthGuard > 12) return null;
      const node = tree.byCode[code];
      if (!node) return null;
      if (node.level != null) {
        for (const a of classAncestors(code)) add.add(classKey(a));
        add.add(classKey(code));
        return classKey(code);
      }
      const u = tree.usedBy[code]?.[0];
      if (!u) return null;
      const parentKey = ensureRevealed(u.owner, add, depthGuard + 1);
      if (!parentKey) return null;
      add.add(parentKey);
      return partKey(parentKey, u.slot, code);
    },
    [tree, classAncestors],
  );

  const revealParent = useCallback(
    (owner: string, slot: string, target: string) => {
      const add = new Set(expanded);
      const parentKey = ensureRevealed(owner, add);
      if (!parentKey) return;
      add.add(parentKey);
      const k = partKey(parentKey, slot, target);
      setExpanded(add);
      setWhereOpen(null);
      setFlash(k);
    },
    [expanded, ensureRevealed],
  );

  const expandAllClass = () => {
    if (!tree) return;
    const all = new Set<string>();
    for (const n of Object.values(tree.byCode)) {
      if (n.children.length > 0 || n.slots.length > 0 || n.attrs.length > 0 || n.refs.length > 0) {
        all.add(classKey(n.code));
      }
    }
    setExpanded(all);
  };
  const collapseAll = () => tree && setExpanded(new Set(baselineRoots()));

  // Search → expand path to matches, flash first.
  const runSearch = (q: string) => {
    setQuery(q);
    if (!tree || !q.trim()) return;
    const needle = q.trim().toLowerCase();
    const add = new Set(expanded);
    let first: string | null = null;
    for (const n of Object.values(tree.byCode)) {
      if (n.code.toLowerCase().includes(needle) || n.label.toLowerCase().includes(needle)) {
        const k = ensureRevealed(n.code, add);
        if (k && !first) first = k;
      }
    }
    setExpanded(add);
    if (first) setFlash(first);
  };

  // scroll to flashed row
  useEffect(() => {
    if (!flash) return;
    const el = scrollRef.current?.querySelector(`[data-key="${CSS.escape(flash)}"]`) as HTMLElement | null;
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    const tm = setTimeout(() => setFlash(null), 1600);
    return () => clearTimeout(tm);
  }, [flash, expanded]);

  const matchSet = useMemo(() => {
    const s = new Set<string>();
    if (!query.trim() || !tree) return s;
    const n = query.trim().toLowerCase();
    for (const node of Object.values(tree.byCode))
      if (node.code.toLowerCase().includes(n) || node.label.toLowerCase().includes(n)) s.add(node.code);
    return s;
  }, [query, tree]);

  // A tree row is hidden when its dimension's toggle is off. The two dimensions
  // are independent: synthetic K/T/F types (kind) and provisional nodes (synthetic
  // flag). Applied to roots AND classification children (synthetic:true nodes like
  // 324 sit deep in the tree, not at the root). Composition parts stay reachable.
  const isHidden = useCallback(
    (code: string): boolean => {
      const node = tree?.byCode[code];
      if (!node) return false;
      if (!showSynthetic && SYNTHETIC_KINDS.has(node.kind)) return true;
      if (!showPending && node.synthetic) return true;
      return false;
    },
    [tree, showSynthetic, showPending],
  );
  const visibleRoots = useMemo(
    () => (tree ? tree.roots.filter((c) => !isHidden(c)) : []),
    [tree, isHidden],
  );
  // Totals for the toggle badges (how much each filter is currently hiding).
  const counts = useMemo(() => {
    let synthetic = 0;
    let pending = 0;
    if (tree) {
      for (const n of Object.values(tree.byCode)) {
        if (SYNTHETIC_KINDS.has(n.kind)) synthetic += 1;
        if (n.synthetic) pending += 1;
      }
    }
    return { synthetic, pending };
  }, [tree]);

  const ctx: RowCtx | null = tree
    ? { tree, expanded, toggle, whereOpen, setWhereOpen, revealParent, flash, matchSet, isHidden }
    : null;

  return (
    <TooltipProvider>
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        <Section title={t("administrationPage.din276Tree", "DIN 276 Kategorien")}>
          {!tree && !error && (
            <div className="flex min-w-0 flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                {t("din276Tree.loading", "Lade Metamodell")}
                {raw ? ` · ${raw.length} ${t("din276Tree.rows", "Zeilen")}` : "…"}
              </p>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-[320px] w-full" />
            </div>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription className="min-w-0 break-words">{error}</AlertDescription>
            </Alert>
          )}

          {tree && ctx && (
            <>
              <div className="din276tree-toolbar flex min-w-0 flex-wrap items-end gap-3">
                <div className="flex w-[min(360px,100%)] flex-col gap-1.5">
                  <Label htmlFor={searchId}>{t("din276Tree.search", "Code oder Label suchen…")}</Label>
                  <div className="relative">
                    <Search
                      aria-hidden
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    />
                    <Input
                      id={searchId}
                      type="search"
                      className="pl-9"
                      placeholder={t("din276Tree.search", "Code oder Label suchen…")}
                      value={query}
                      onChange={(event) => runSearch(event.target.value)}
                    />
                  </div>
                </div>
                <Button variant="outline" className="gap-2" onClick={expandAllClass}>
                  <ChevronsUpDown aria-hidden className="h-4 w-4" />
                  {t("din276Tree.expandAll", "Alle auf")}
                </Button>
                <Button variant="outline" className="gap-2" onClick={collapseAll}>
                  <ChevronsDownUp aria-hidden className="h-4 w-4" />
                  {t("din276Tree.collapseAll", "Alle zu")}
                </Button>

                {/* Two INDEPENDENT post-apply HITL filters. Default off = only the
                    productive, confirmed DIN-276 categories are shown. */}
                <Button
                  variant={showSynthetic ? "default" : "outline"}
                  className="gap-2"
                  onClick={() => setShowSynthetic((v) => !v)}
                  title={t("din276Tree.toggleSyntheticHint", "Synthetische Typen K./F./T. (Komponenten/Topologie/Funktionen) ein-/ausblenden")}
                  aria-pressed={showSynthetic}
                >
                  <Box aria-hidden className="h-4 w-4" />
                  {t("din276Tree.toggleSynthetic", "Synthetische Typen")}
                  {counts.synthetic > 0 && (
                    <span className="rounded-full bg-muted px-1.5 text-xs font-semibold text-muted-foreground">
                      {counts.synthetic}
                    </span>
                  )}
                </Button>
                <Button
                  variant={showPending ? "default" : "outline"}
                  className="gap-2"
                  onClick={() => setShowPending((v) => !v)}
                  title={t("din276Tree.togglePendingHint", "Provisorische Platzhalter-Knoten (noch nicht bestätigt) ein-/ausblenden")}
                  aria-pressed={showPending}
                >
                  <Sigma aria-hidden className="h-4 w-4" />
                  {t("din276Tree.togglePending", "Provisorisch")}
                  {counts.pending > 0 && (
                    <span className="rounded-full bg-muted px-1.5 text-xs font-semibold text-muted-foreground">
                      {counts.pending}
                    </span>
                  )}
                </Button>

                <span className="ml-auto text-xs text-muted-foreground">
                  {tree.nodeCount} {t("din276Tree.nodes", "Knoten")} · {tree.slotCount} Slots
                </span>
              </div>

              <div className="din276tree-legend mt-3 text-xs text-muted-foreground">
                <span><Folder size={13} className="din276tree-icn icn-folder" /> {t("din276Tree.classification", "Kostengruppe")}</span>
                <span><Package size={13} className="din276tree-icn icn-element" /> {t("din276Tree.element", "Element")}</span>
                <span><Box size={13} className="din276tree-icn icn-component" /> {t("din276Tree.component", "Komponente")}</span>
                <span><Frame size={13} className="din276tree-icn icn-locus" /> {t("din276Tree.locus", "Topologie")}</span>
                <span><Sigma size={13} className="din276tree-icn icn-funktion" /> {t("din276Tree.funktion", "Funktion")}</span>
                <span><Boxes size={13} className="din276tree-particon" /> {t("din276Tree.parts", "Teile")}</span>
              </div>

              {visibleRoots.length === 0 ? (
                // Beide Filter aus und trotzdem nichts sichtbar heißt: Es gibt
                // nur synthetische bzw. provisorische Knoten — das ist ein
                // Befund, kein leerer Baum.
                <div className="mt-3 flex flex-col items-center gap-2 py-6 text-center">
                  <p className="text-sm font-semibold">
                    {t("din276Tree.emptyTitle", "Keine produktiven Kostengruppen sichtbar")}
                  </p>
                  <p className="max-w-prose text-sm text-muted-foreground">
                    {t(
                      "din276Tree.emptyHint",
                      "Die beiden Filter blenden synthetische Typen und provisorische Knoten aus. Schalte sie ein, um den vollständigen Graphen zu sehen.",
                    )}
                  </p>
                </div>
              ) : (
                <div className="din276tree mt-3 rounded-md border" ref={scrollRef}>
                  {visibleRoots.map((code) => (
                    <Row key={code} ctx={ctx} code={code} depth={0} nodeKey={classKey(code)} isPart={false} seen={new Set()} />
                  ))}
                </div>
              )}
            </>
          )}
        </Section>
      </div>
    </TooltipProvider>
  );
}
